import { Constants } from "./constants";

export interface IShowTypeLabel {
  /** Full human-readable display name */
  label: string;
  /** Short abbreviation safe for tight spaces (2–4 chars) */
  short: string;
}

/**
 * Canonical display labels and short-form abbreviations for every show type.
 * Keyed by the raw Constants.show_type value string.
 *
 * Usage:
 *   import { getShowTypeShort, getShowTypeLabel } from "@eagerdog/constants";
 *   getShowTypeShort("Nosework")        // → "NW"
 *   getShowTypeLabel("Rally Obedience") // → "Rally Obedience"
 */
export const ShowTypeLabels = new Map<string, IShowTypeLabel>([
  [Constants.show_type.conformation,          { label: "Conformation",              short: "CONF" }],
  [Constants.show_type.nosework,              { label: "Nosework",                  short: "NW"   }],
  [Constants.show_type.sniff_n_go,            { label: "Sniff-n-Go",                short: "SNG"  }],
  [Constants.show_type.obedience,             { label: "Obedience",                 short: "OBE"  }],
  [Constants.show_type.rally_obedience,       { label: "Rally Obedience",           short: "RAL"  }],
  [Constants.show_type.agility,               { label: "Agility",                   short: "AGI"  }],
  [Constants.show_type.weight_pull,           { label: "Weight Pull",               short: "WP"   }],
  [Constants.show_type.urban_rat_race,        { label: "Urban Rat Race",            short: "URR"  }],
  [Constants.show_type.country_rat_race,      { label: "Country Rat Race",          short: "CRR"  }],
  [Constants.show_type.scent_dog_games,       { label: "Scent Dog Games",           short: "SDG"  }],
  [Constants.show_type.lost_and_found,        { label: "Lost and Found",            short: "L&F"  }],
  [Constants.show_type.scentwork,             { label: "Scentwork",                 short: "SCW"  }],
  [Constants.show_type.scent,                 { label: "Scent",                     short: "SCT"  }],
  [Constants.show_type.scent_detection,       { label: "Scent Detection",           short: "SD"   }],
  [Constants.show_type.cwags_games,           { label: "Games",                     short: "GAM"  }],
  [Constants.show_type.fast_cat,              { label: "Fast CAT",                  short: "FCAT" }],
  [Constants.show_type.fast_dash,             { label: "Fast Dash",                 short: "FD"   }],
  [Constants.show_type.cat,                   { label: "CAT",                       short: "CAT"  }],
  [Constants.show_type.fetch,                 { label: "Fetch",                     short: "FET"  }],
  [Constants.show_type.lure_coursing,         { label: "Lure Coursing",             short: "LC"   }],
  [Constants.show_type.precision_coursing,    { label: "Precision Coursing",        short: "PC"   }],
  [Constants.show_type.dock_jumping,          { label: "Dock Jumping",              short: "DJ"   }],
  [Constants.show_type.dock_diving,           { label: "Dock Diving",               short: "DD"   }],
  [Constants.show_type.cdsp_video,            { label: "CDSP Video",                short: "CDSP" }],
  [Constants.show_type.shed_dog,              { label: "Shed Dog",                  short: "SHD"  }],
  [Constants.show_type.water_dog,             { label: "Water Dog",                 short: "WTR"  }],
  [Constants.show_type.farm_dog,              { label: "Farm Dog",                  short: "FRM"  }],
  [Constants.show_type.att,                   { label: "Temperament Test (ATT)",    short: "ATT"  }],
  [Constants.show_type.canine_good_citizen,   { label: "Canine Good Citizen",       short: "CGC"  }],
  [Constants.show_type.trick_dog,             { label: "Trick Dog",                 short: "TRK"  }],
  [Constants.show_type.herding,               { label: "Herding",                   short: "HRD"  }],
  [Constants.show_type.pre_trial_herding,     { label: "Pre-Trial (Herding)",       short: "PTH"  }],
  [Constants.show_type.instinct_test_herding, { label: "Instinct Test (Herding)",   short: "ITH"  }],
  [Constants.show_type.barn_hunt,             { label: "Barn Hunt",                 short: "BH"   }],
  [Constants.show_type.on_leash_run_through,  { label: "On Leash Run Through",      short: "OLRT" }],
  [Constants.show_type.obedience_show_n_go,   { label: "Obedience Show N Go",       short: "OSNG" }],
  [Constants.show_type.general_rally,         { label: "Rally Show N Go",            short: "RSNG" }],
  [Constants.show_type.field_trial,           { label: "Field Trial",               short: "FT"   }],
  [Constants.show_type.ukc_obedience,         { label: "UKC Obedience",             short: "UKCO" }],
  [Constants.show_type.ukc_rally,             { label: "UKC Rally",                 short: "UKCR" }],
  [Constants.show_type.speedway,              { label: "Speedway",                  short: "SPD"  }],
  [Constants.show_type.nitrous_50,            { label: "Nitrous 50",                short: "N50"  }],
  [Constants.show_type.nitrous_100,           { label: "Nitrous 100",               short: "N100" }],
]);

/**
 * Returns the short-form abbreviation for a show type.
 * Falls back to the first 4 characters uppercased if the type is not in the map.
 */
export function getShowTypeShort(showType: string): string {
  return ShowTypeLabels.get(showType)?.short ?? showType.substring(0, 4).toUpperCase();
}

/**
 * Returns the full display label for a show type.
 * Falls back to the raw value if the type is not in the map.
 */
export function getShowTypeLabel(showType: string): string {
  return ShowTypeLabels.get(showType)?.label ?? showType;
}
